import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from './SeoHead';
import {
  CUSTOMER_POLICY_PAGES,
  CUSTOMER_POLICY_ROUTES,
  CUSTOMER_SUPPORT,
} from '../data/customerPolicies';
import '../styles/TermsPolicy.css';

const section = (pageKey, heading) => {
  const page = CUSTOMER_POLICY_PAGES.find((item) => item.key === pageKey);
  return page?.sections.find((item) => item.heading === heading);
};

const GROUPS = [
  {
    id: 'pricing',
    title: 'Pricing & Fare Calculation',
    sections: [
      section('fare', 'How estimated fare is calculated'),
      section('fare', 'Billing distance'),
      section('fare', 'Peak and festival pricing'),
    ],
  },
  {
    id: 'booking',
    title: 'Booking & Confirmation',
    sections: [
      section('terms', 'Booking requests'),
      section('booking', 'Booking requests'),
      section('booking', 'My Bookings for signed-in customers'),
      section('booking', 'Guest bookings'),
      section('booking', 'Changes to an existing booking'),
      section('booking', 'Related policies'),
    ],
  },
  {
    id: 'cancellation',
    title: 'Cancellation & Refund',
    sections: [
      section('cancellation', 'Current cancellation handling'),
      section('cancellation', 'How to cancel'),
      section('cancellation', 'Fees and refunds'),
      section('cancellation', 'Contact'),
    ],
  },
  {
    id: 'conduct',
    title: 'Passenger Conduct',
    sections: [section('terms', 'Passenger conduct')],
  },
  {
    id: 'payment',
    title: 'Payment & Safety',
    sections: [
      section('terms', 'Payment & safety'),
      section('fare', 'Payment'),
    ],
  },
  {
    id: 'extras',
    title: 'Luggage, Waiting & Additional Charges',
    sections: [section('fare', 'Extra charges')],
  },
];

const PolicyBlocks = ({ sections }) => (
  <>
    {sections.filter(Boolean).map((item) => (
      <div key={item.heading} className="terms-acc-block">
        <h3>{item.heading}</h3>
        {(item.paragraphs || []).map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        {item.list ? (
          <ul>
            {item.list.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        ) : null}
      </div>
    ))}
  </>
);

const TermsAccordion = ({ group }) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const panelId = `${group.id}-panel`;
  const buttonId = `${group.id}-button`;

  useEffect(() => {
    if (panelRef.current) panelRef.current.inert = !open;
  }, [open]);

  return (
    <div className="terms-acc">
      <button
        type="button"
        id={buttonId}
        className="terms-acc-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{group.title}</span>
        <i className={`fa fa-chevron-down terms-acc-chevron${open ? ' is-open' : ''}`} aria-hidden="true"></i>
      </button>
      <div
        id={panelId}
        ref={panelRef}
        role="region"
        aria-labelledby={buttonId}
        aria-hidden={!open}
        className={`terms-acc-panel${open ? ' is-open' : ''}`}
      >
        <div className="terms-acc-panel-inner">
          <PolicyBlocks sections={group.sections} />
        </div>
      </div>
    </div>
  );
};

const TermsPolicyView = ({ page, related }) => {
  const about = section('terms', 'About these terms');
  const relatedPolicies = section('terms', 'Related policies');
  const contact = section('terms', 'Contact');

  return (
    <main className="terms-policy">
      <SeoHead title={page.title} description={page.description} path={page.path} />

      <section className="terms-policy-hero" aria-labelledby="terms-policy-heading">
        <div className="container">
          <h1 id="terms-policy-heading">{page.heading}</h1>
          <p>Customer policy for EasyDropTaxi website and Customer App bookings.</p>
        </div>
      </section>

      <section className="terms-policy-body block-space">
        <div className="container terms-policy-content">
          <p className="terms-updated"><strong>Last updated:</strong> {page.lastUpdated}</p>
          <PolicyBlocks sections={[about]} />

          <div className="terms-acc-list">
            {GROUPS.map((group) => (
              <TermsAccordion key={group.id} group={group} />
            ))}
          </div>

          <PolicyBlocks sections={[relatedPolicies, contact]} />

          <div className="terms-contact-panel">
            <p>
              <strong>Email:</strong>{' '}
              <a href={CUSTOMER_SUPPORT.mailto}>{CUSTOMER_SUPPORT.email}</a>
            </p>
            <p>
              <strong>Phone:</strong>{' '}
              <a href={CUSTOMER_SUPPORT.phoneTel}>{CUSTOMER_SUPPORT.phoneDisplay}</a>
            </p>
          </div>

          <h2>Other customer policies</h2>
          <ul>
            {related.map((item) => (
              <li key={item.path}>
                <Link to={item.path}>{item.heading}</Link>
              </li>
            ))}
            <li>
              <Link to={CUSTOMER_POLICY_ROUTES.accountDeletion}>
                Customer Account &amp; Data Deletion
              </Link>
            </li>
          </ul>
        </div>
      </section>
    </main>
  );
};

export default TermsPolicyView;
